import { PrismicRichText } from '@prismicio/react';
import IconArrowDown from '@remote-com/norma/icons/arrow-down.svg';
import { useRef } from 'react';
import { Button } from 'remote-com-shared/components/Button';
import ContentContainer from 'remote-com-shared/components/ContentContainer';
import HeaderContext from 'remote-com-shared/components/Header/context';
import MenuEntry from 'remote-com-shared/components/HeaderNavigation/MenuEntry';
import MenuItem from 'remote-com-shared/components/HeaderNavigation/MenuItem';
import {
  NavigationPrimary,
  NavigationSecondary,
} from 'remote-com-shared/components/HeaderNavigation/styles/Navigation';
import { Hero as HeroComponent } from 'remote-com-shared/components/Hero';
import { Main, ContentMain, Title, Description } from 'remote-com-shared/components/Hero/styles';
import { FormContainer } from 'remote-com-shared/components/ModalForm/styles';
import { TwoStepForm, TwoStepFormModalButton } from 'remote-com-shared/components/TwoStepForm';
import { StyledButton, SubmitButton } from 'remote-com-shared/components/TwoStepForm/styles';
import { reasonOptions } from 'remote-com-shared/data';
import { fluidProp } from 'remote-com-shared/style/helpers';
import styled, { css } from 'styled-components';

import BgIllustration from './assets/bg-illustration.svg';

import { transformProperties } from '@/src/domains/prismic/helpers';
import {
  tabletBreakpoint,
  desktopBreakpoint,
  mobileBreakpoint,
} from '@/src/domains/shared/breakpoints';
import { Header } from '@/src/views/SharedEvents/Header';

const Hero = styled(HeroComponent)`
  font-family: var(--typography-fontBrand);
  background: var(--colors-darkBlue);
  position: relative;
  padding-top: 0;

  ${Title} {
    ${fluidProp('font-size', '34px', '60px')}
  }

  ${ContentContainer} {
    position: unset;
  }

  ${Main} {
    z-index: 1;
  }

  ${Description} {
    color: var(--colors-blank);
  }

  ${FormContainer} {
    width: 45%;

    ${desktopBreakpoint(css`
      width: 60%;
    `)}

    ${tabletBreakpoint(css`
      width: 90%;
    `)}


    ${mobileBreakpoint(css`
      width: 100%;
    `)}
  }

  ${StyledButton}, ${SubmitButton} {
    background-color: #dfefd3;
    color: #01244b;
  }

  ${ContentMain} {
    width: 55%;
    justify-content: center;
    display: flex;
    flex-direction: column;

    ${desktopBreakpoint(css`
      width: 40%;
    `)}

    ${tabletBreakpoint(css`
      width: 100%;
      margin: 50px 0;
    `)}
  }
`;

export const BgIllustrationWrapper = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  z-index: -1;

  svg {
    vertical-align: bottom;
  }

  ${desktopBreakpoint(css`
    display: none;
  `)}
`;

export const ConfettiIllustrationWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 0;

  ${tabletBreakpoint(css`
    display: none;
  `)}
`;

export const CTAButton = styled(Button)`
  && {
    background: #dfefd3;
    color: #01244b;
    text-transform: uppercase;
  }
`;

export const IconDropdown = styled(IconArrowDown)`
  width: 18px;
  margin-left: 12px;
`;

export const ScrollButton = styled.a`
  && {
    color: var(--colors-blank);
    text-transform: uppercase;
    display: flex;
  }
`;

export const ActionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

export function HeroSlice({ slice }) {
  const { primary, context } = transformProperties(slice);
  const { title, description, ctaLabel, readMore } = primary;
  const headerRef = useRef(null);
  const { hubspotFormDefinition, g2LeaderPageData } = context;

  const { nav, ctaLink } = g2LeaderPageData;

  const scheduleDemoReasonFields = reasonOptions.slice(0, 2);
  return (
    <>
      <HeaderContext.Provider value={headerRef}>
        <Header ref={headerRef} hideBanner useRemoteFont>
          <NavigationPrimary>
            {nav.map(({ navItemLabel, navItemLink }, index) => (
              <MenuEntry key={index}>
                <MenuItem as="a" href={`#${navItemLink}`}>
                  {navItemLabel}
                </MenuItem>
              </MenuEntry>
            ))}
          </NavigationPrimary>

          <NavigationSecondary>
            {ctaLink && (
              <TwoStepFormModalButton
                {...{ hubspotFormDefinition, scheduleDemoReasonFields }}
                button={{ as: Button }}
                size="lg"
              >
                {ctaLink}
              </TwoStepFormModalButton>
            )}
          </NavigationSecondary>
        </Header>
        <Hero
          title={<PrismicRichText field={title} />}
          description={<PrismicRichText field={description} />}
          aside={
            <TwoStepForm
              formTitle="Start employing anywhere"
              formDescription="We'll contact you to schedule a call, answer any questions you may have, and start onboarding your employees."
              hubspotFormDefinition={hubspotFormDefinition}
              formID="hero-two-step-form"
              scheduleDemoReasonFields={scheduleDemoReasonFields}
              companyEmailAddressesOnly
            />
          }
          actions={
            <ActionsWrapper>
              {ctaLabel && (
                <TwoStepFormModalButton button={{ as: CTAButton }}>
                  {ctaLabel}
                </TwoStepFormModalButton>
              )}
              <ScrollButton href="#quote">
                {readMore || 'Read more'} <IconDropdown />
              </ScrollButton>
            </ActionsWrapper>
          }
          preview="0vh"
        >
          <BgIllustrationWrapper>
            <BgIllustration />
          </BgIllustrationWrapper>
        </Hero>
      </HeaderContext.Provider>
    </>
  );
}
